import { Component, OnInit, ViewChild, ElementRef, isDevMode, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MultiInputStepComponent } from '../../step/multi-input-step/multi-input-step.component';
import { ControlContainer, FormGroupDirective, UntypedFormControl, Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-zip-code-street-address',
    templateUrl: './zip-code-street-address.component.html',
    styleUrls: ['./zip-code-street-address.component.scss', '../../step/step.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    standalone: false
})
export class ZipCodeStreetAddressComponent extends MultiInputStepComponent implements OnInit {
  @ViewChild('zipCode') zipCodeField: ElementRef;
  @ViewChild('streetAddress') streetAddressField: ElementRef;
 
  protected propertyTypeName: String;
  public experiment: Boolean;
  public notDebtSettlement: Boolean;
  public subtext: String;
  public zipTitle: String = "What's your zip code?";
  public streetTitle: String = "Street address";
  // hideStreetAddress = false;
  showAddressInput = false;
  public useUpdatedInputs: boolean = false;
  // FRU-25232 (Matomo A/B FRU-25232-prefill-zip, variation 'prefill-zip'), as Hope's ZIP step (FRU-24551, FRU-24564): an
  // empty ZIP box shows the ZIP Cloudflare places the visitor in (public_api/ip_state, US only) as its light-gray placeholder.
  // The box itself stays empty: typing just types, and Enter or Continue on the empty box takes it (takeOfferedZip).
  public prefillZipTest = false;
  public readonly zipOfferHint = 'Based on your location. Press Continue to use it, or type your ZIP.';
  /** FRU-25232: the ZIP on offer ('' when none). */
  public offeredZip = '';
  private visitorZip = '';
  private visitorZipState = '';
  private visitorZipRequested = false;
  /** FRU-25232: the visitor typed, or took the offer; it doesn't show again. */
  private offerSettled = false;
  private offerOutcomeTracked = false;
  /** FRU-25232: the street opened by taking the offered ZIP shows no error until the visitor submits again. */
  private streetQuietUntilSubmit = false;
  private arrived = false;
  private watchingStateForOffer = false;
  /** FRU-25232: re-check Continue above the keyboard (an error under a field pushes it down). */
  private revealContinueSoon: () => void = () => {};
  private destroyRef = inject(DestroyRef);

  private syncUpdatedInputsFromUrl(): void {
    this.useUpdatedInputs = typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('startAssistant') === '0';
  }
  
  ngOnInit() {
    this.syncUpdatedInputsFromUrl();
    // Reuse existing controls after AI switch-back; addControl() keeps the old FormGroup control,
    // while this component would otherwise validate against a new blank local control.
    const zipControl = this.form.contains('zipCode')
      ? this.form.get('zipCode') as UntypedFormControl
      : new UntypedFormControl('');
    zipControl.setValidators([Validators.required, Validators.pattern('^[0-9]{5}$')]);
    zipControl.updateValueAndValidity();
    this.controls.push(zipControl);

    const streetAddressControl = this.form.contains('streetAddress')
      ? this.form.get('streetAddress') as UntypedFormControl
      : new UntypedFormControl('');
    streetAddressControl.setValidators([Validators.required, Validators.pattern('^[\\s]*[A-Za-z0-9,#.\'-]+[A-Za-z0-9\\s,#.\'-]*$')]);
    streetAddressControl.updateValueAndValidity();
    this.controls.push(streetAddressControl);

    if (!this.form.contains('zipCode')) { this.form.addControl('zipCode', this.controls[0]); }
    if (!this.form.contains('streetAddress')) { this.form.addControl('streetAddress', this.controls[1]); }
    this.showAddressInput = !!this.controls[1].value;
    const type = this.form.get('type')?.value || '';
    const formType = this.form.get('formType')?.value || '';
    this.subtext = type == 'personalloan' ? '' : 'Used to verify home value';
    this.notDebtSettlement = formType != 'debtsettlement' && formType != 'debtrelief';
    this.analyticsService.getExperiments().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (variationData: any) => {
        // if (variationData.enabledVariations.hasOwnProperty('showAddressInput')) {
        //   this.showAddressInput = false;
        // }
        this.setPrefillZipTest(variationData.enabledVariations.hasOwnProperty('prefill-zip'));
      }
    );

    this.keepContinueAboveKeyboard();

    if (!environment.production && this.randomService.fillEnabled) {
      this.form.controls.zipCode.setValue(this.randomService.consumer.zipCode.toString());
      this.form.controls.streetAddress.setValue(this.randomService.consumer.streetAddress);
      this.showAddressInput = true;
    }
  }

  initStep() {
    this.syncUpdatedInputsFromUrl();
    this.arrived = true;
    this.offerZipFromLocation();
    // FAST mode (FRU-21170): reveal the street field up front so the browser can
    // group-fill zip + street together. Chrome won't autofill a lone postal-code
    // field with no address group present (FRU: street is hidden until zip is typed).
    if (this.fastModeHost && this.fastModeHost.enabled) {
      setTimeout(() => { this.showAddressInput = true; }, 0);
    }
    if(this.experiment) {
      // Street address first in experiment
      setTimeout(() => this.streetAddressField.nativeElement.focus(), 0);
    } else {
      // Give zip code focus
      setTimeout(() => this.zipCodeField.nativeElement.focus(), 0);
    }
  }

  zipInput($event) {
    // FRU-25232: what's typed is the visitor's own; the offered ZIP doesn't come back if they clear it.
    if (String($event?.target?.value || '') !== '') { this.offerSettled = true; }
    this.checkZipLength($event.target.value);
  }

  /** FRU-25232: the variation can arrive after the step was created; ip_state is asked once, for the variation only. */
  private setPrefillZipTest(on: boolean): void {
    this.prefillZipTest = on;
    if (!on || this.visitorZipRequested) { return; }
    this.visitorZipRequested = true;
    this.fruHttpService.getStateNew().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r: any) => {
        const zip = String(r?.zip || '').trim();
        if (!/^[0-9]{5}$/.test(zip)) { return; }   // '' outside the US
        const st = String(r?.state || '').toUpperCase();
        this.visitorZip = zip;
        this.visitorZipState = /^[A-Z]{2}$/.test(st) ? st : '';
        // A visitor already on this step when ip_state answers still gets the offer.
        if (this.arrived) { this.offerZipFromLocation(); }
      },
      error: () => { /* the box just starts empty */ },
    });
  }

  /**
   * FRU-25232: offer the visitor's ZIP while the box is empty. It's a guess from the IP, so never over a ZIP already in the
   * box (typed, filled or resumed), and not when the form's state already says somewhere else.
   */
  private offerZipFromLocation(): void {
    if (!this.prefillZipTest || this.offeredZip || this.offerSettled || !this.visitorZip) { return; }
    if (String(this.controls[0]?.value || '').trim() !== '') { return; }
    const formState = String(this.form.get('propertyState')?.value || '').toUpperCase();
    if (formState && this.visitorZipState && formState !== this.visitorZipState) { return; }
    this.offeredZip = this.visitorZip;
    this.analyticsService.trackEvent('RatesForm', 'zip_prefill', 'shown');
    this.watchStateForOffer();
  }

  /** FRU-25232: an offer the visitor hasn't taken is withdrawn when the form's state stops matching it. */
  private watchStateForOffer(): void {
    const stateControl = this.form.get('propertyState');
    if (this.watchingStateForOffer || !stateControl) { return; }
    this.watchingStateForOffer = true;
    stateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v: any) => {
      const st = String(v || '').toUpperCase();
      if (!this.offerPending() || !st || !this.visitorZipState || st === this.visitorZipState) { return; }
      this.offeredZip = '';
      this.analyticsService.trackEvent('RatesForm', 'zip_prefill', 'withdrawn');
    });
  }

  /** FRU-25232: the offered ZIP is showing as the placeholder (on offer, the box empty, not typed in or taken). */
  get zipOfferShown(): boolean {
    return this.offerPending();
  }

  /**
   * FRU-25232: the ZIP error, but not while the offer shows. Clicking Continue blurs the empty box (touched) a moment before
   * the click takes the offered ZIP, which flashed "Please enter a valid 5 digit US zip code".
   */
  get zipErrorShown(): boolean {
    return !!(this.controls[0]?.touched && this.controls[0]?.errors) && !this.zipOfferShown;
  }

  /**
   * FRU-25232: the street error, but not for a street box the offered ZIP opened (with the cursor in it) until the visitor
   * submits again. Leaving it to fix the ZIP marked it touched and showed "Please fill out your valid street address".
   */
  get streetErrorShown(): boolean {
    return !!(this.controls[1]?.touched && this.controls[1]?.errors) && !this.streetQuietUntilSubmit;
  }

  private offerPending(): boolean {
    return !!this.offeredZip && !this.offerSettled && String(this.controls[0]?.value || '').trim() === '';
  }

  /** FRU-25232: Enter or Continue on the empty box takes the offered ZIP. Returns true when it did. */
  private takeOfferedZip(): boolean {
    if (!this.offerPending()) { return false; }
    this.controls[0].setValue(this.offeredZip);
    this.offerSettled = true;
    return true;
  }

  /**
   * FRU-25232: on a phone the keyboard covered Continue (Galaxy S23: 355px left above it, Continue 100px below), leaving
   * only the keyboard's Go. While a field of this step has focus and a keyboard is open, scroll Continue up to just above
   * the keyboard, never past the field being typed in. Runs when the keyboard opens, when focus moves (the street opens) and
   * after each submit (an error shows).
   */
  private keepContinueAboveKeyboard(): void {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) { return; }
    const host: HTMLElement = this.elementRef.nativeElement;
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.revealContinueAboveKeyboard());
    };
    this.revealContinueSoon = schedule;
    vv.addEventListener('resize', schedule);
    host.addEventListener('focusin', schedule);
    this.destroyRef.onDestroy(() => {
      cancelAnimationFrame(frame);
      vv.removeEventListener('resize', schedule);
      host.removeEventListener('focusin', schedule);
    });
  }

  /**
   * FRU-25273: the rates form scrolls to its top when a step's fade-in ends, which undoes a reveal the opening keyboard
   * already made (Galaxy S24: Continue stayed 38px under the keyboard on arrival). It calls this after that scroll.
   */
  afterFormScroll(): void {
    this.revealContinueSoon();
  }

  revealContinueAboveKeyboard(): void {
    const vv = window.visualViewport;
    const host: HTMLElement = this.elementRef.nativeElement;
    const field = document.activeElement as HTMLElement;
    const button = host.querySelector('button.continue') as HTMLElement;
    if (!vv || !button || !field || field.tagName !== 'INPUT' || !host.contains(field)) { return; }
    const by = ZipCodeStreetAddressComponent.scrollToRevealContinue({
      innerHeight: window.innerHeight,
      viewportTop: vv.offsetTop,
      viewportHeight: vv.height,
      viewportScale: vv.scale,
      fieldTop: field.getBoundingClientRect().top,
      buttonBottom: button.getBoundingClientRect().bottom,
    });
    if (by > 0) { window.scrollBy(0, by); }
  }

  /** FRU-25232: how far to scroll so Continue sits above the keyboard (0: no keyboard, already visible, or pinch-zoomed). */
  static scrollToRevealContinue(m: { innerHeight: number, viewportTop: number, viewportHeight: number, viewportScale: number,
    fieldTop: number, buttonBottom: number }): number {
    const gap = 8;
    // A keyboard takes 150px or more; a smaller visible area is browser chrome. Pinch zoom also shrinks it: leave that alone.
    if (m.innerHeight - m.viewportHeight < 150 || Math.abs(m.viewportScale - 1) > 0.01) { return 0; }
    const hidden = m.buttonBottom + gap - (m.viewportTop + m.viewportHeight);
    const fieldRoom = m.fieldTop - m.viewportTop - gap;
    return Math.max(0, Math.min(hidden, fieldRoom));
  }

  zipChange($event) {
    this.snap();
    this.checkZipLength($event.target.value);
  }

  onZipEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    setTimeout(() => this.streetAddressField && this.streetAddressField.nativeElement.focus(), 0);
  }

  checkZipLength(value: string) {
    if(value.length >= 5) {
      const wasHidden = !this.showAddressInput;
      setTimeout(() => { this.showAddressInput = true; }, 0);
      if (wasHidden && this.isFullFastFlow) {
        setTimeout(() => this.streetAddressField && this.streetAddressField.nativeElement.focus(), 0);
      }
    }
  }

  focusControl(name: string) {
    const els = Array.from(document.querySelectorAll('[formcontrolname="' + name + '"]')) as HTMLElement[];
    const el = els.find(e => (e as any).offsetParent !== null) || els[0];
    if (el) { setTimeout(() => el.focus(), 0); }
  }

  submit() {
    this.revealContinueSoon();
    const zipC = this.form.get('zipCode');
    const streetC = this.form.get('streetAddress');
    // FRU-25232: taking the offered ZIP opens the street quietly (no error yet), as typing five digits does.
    if (this.takeOfferedZip() && streetC && streetC.invalid) {
      this.streetQuietUntilSubmit = true;
      setTimeout(() => {
        this.showAddressInput = true;
        setTimeout(() => this.streetAddressField && this.streetAddressField.nativeElement.focus(), 0);
      }, 0);
      return;
    }
    this.streetQuietUntilSubmit = false;
    if (zipC) { zipC.markAsTouched(); if (zipC.invalid) { this.focusControl('zipCode'); return; } }
    if (streetC) { streetC.markAsTouched(); if (streetC.invalid) { setTimeout(() => { this.showAddressInput = true; }, 0); this.focusControl('streetAddress'); return; } }
    if (this.offeredZip && !this.offerOutcomeTracked) {
      this.offerOutcomeTracked = true;
      this.analyticsService.trackEvent('RatesForm', 'zip_prefill', this.controls[0].value === this.offeredZip ? 'kept' : 'changed');
    }
    // Get zip details
    this.fruHttpService.getZipDetails(this.controls[0].value, this.form.controls.type.value, this.form.controls.creditRating.value).subscribe((response) => {
      // if (response.enabled === false) {
      //   // Redirect if the state comes back disabled
      //   if (isDevMode()) {
      //     console.log('Zip code disabled state redirect');
      //   }
      //   this.fruHttpService.redirectRateTables(this.form);
      // } else if (response.lender && parseInt(response.lender) === 0) {
      //   // Redirect if lenders comes back as 0
      //   if (isDevMode()) {
      //     console.log('Zip code no lender redirect');
      //   }
      //   this.fruHttpService.redirectRateTables(this.form);
      // } else {
        // Populate state and city
        this.form.controls.propertyState.setValue(response.state || '');
        this.form.controls.propertyCity.setValue(response.city || '');
      // }
    });

    super.submit();
  }
}
